/**
 * The authoring loop (Wave C lane C3, docs/AUTHORING.md): a user's intent in a thread becomes a program for one
 * target, checked by the target's door as compiler errors (JSON pointers), repaired by the model within a bounded
 * number of rounds, previewed, and proposed for approval in dry run. These are the job payload and the message and
 * approval bodies the runtime writes into `conversations.*`; C5 renders them.
 */
import { z } from 'zod'
import { Uuid } from '../primitives.js'
import { Venue } from '../enums.js'
import { JsonObject } from './common.js'

/**
 * Program targets: `atc`, the platform's one strategy language, is the only one authored. The others are retired and
 * stay readable in the threads' history, never authored again (a request naming one gets `atc`): `fdl/v1` (the first
 * language, retired by RETIRE1: an approval of it still open fails `LANGUAGE_RETIRED`), `trend-algorithm/v1` (the
 * door's algorithm routes left with CUT2) and `algoboo-strategy/v3` (algo.boo's own spec, BOO2).
 */
export const AUTHORING_TARGET_IDS = ['trend-algorithm/v1', 'algoboo-strategy/v3', 'fdl/v1', 'atc'] as const
export const AuthoringTargetId = z.enum(AUTHORING_TARGET_IDS)
export type AuthoringTargetId = z.output<typeof AuthoringTargetId>

/** Model repairs after the first draft (so at most `1 + AUTHORING_MAX_REPAIRS` model calls per run). */
export const AUTHORING_MAX_REPAIRS = 4
/** The only mode the loop proposes in; live trading needs the trial gate (ARCHITECTURE-V4 §4.2). */
export const AUTHORING_MODE = 'dry_run' as const

/** A place in a text program (`atc`): 1-based line and column, byte range (sf-atc `Problem`). */
export const CompileSpan = z.object({ line: z.number().int(), column: z.number().int(), start: z.number().int(), end: z.number().int() })
export type CompileSpan = z.output<typeof CompileSpan>

/**
 * One compiler error: `pointer` is an RFC 6901 JSON pointer into the program (`/definition/terms/2/feature`; `''` is
 * the whole program), `code` the checker's stable code (the door's own codes verbatim: `unknown_feature`,
 * `no_baseline`, …, or ours: `schema`, `spec`, `CAPABILITY_MISSING`, `UNGROUNDED_ADDRESS`, …), `hint` what to do.
 */
export const CompileError = z.object({
  pointer: z.string(),
  code: z.string().min(1).max(64),
  message: z.string().max(1000),
  hint: z.string().max(500).nullable(),
  span: CompileSpan.optional(),
})
export type CompileError = z.output<typeof CompileError>

/** `authoring.run`: enqueued by lane C2's turn for an authoring intent; idempotent by `messageId` (key `authoring:<messageId>`). */
export const AuthoringRunPayload = z.object({
  threadId: Uuid,
  messageId: Uuid,
  agentId: Uuid.optional(),
  /** The user's words: data for the model, never instructions. */
  intent: z.string().trim().min(1).max(4000),
  /** Chosen by code or the UI (a starter); the model can never change it. Default: picked from the intent by rule. */
  target: AuthoringTargetId.optional(),
  /** A template id of `AGENT_TEMPLATE_BUNDLES` (`trend_scout`, …): the program is the template's, no model call. */
  starter: z.string().regex(/^[a-z_-]{3,40}$/).optional(),
  /**
   * The venue of the intent (a desk thread: the venue the user picked; an agent thread: ignored, the agent's venue of
   * record wins). A venue that is not live is the compile error `VENUE_NOT_AVAILABLE`. Default `robinhood`.
   */
  venue: Venue.optional(),
})
export type AuthoringRunPayload = z.output<typeof AuthoringRunPayload>

export const PreviewFact = z.object({ label: z.string(), value: z.string() })
export type PreviewFact = z.output<typeof PreviewFact>

/**
 * What a program would do, before anything runs: `door` (the Intelligence door's list and backtest of the definition,
 * as current as `derivedThroughBlock` / `validAt`), `evaluation` (the develop loop's results on unseen data),
 * `structural` (the native deployments with their parameters in plain words).
 */
export const AuthoringPreview = z.object({
  target: AuthoringTargetId,
  kind: z.enum(['door', 'evaluation', 'structural']),
  title: z.string(),
  lines: z.array(z.string()),
  facts: z.array(PreviewFact),
  /** Door answers: the lowest derivation frontier they read (decimal string). */
  derivedThroughBlock: z.string().nullable(),
  /** Door answers: the chain time the list is valid at (the trend point). */
  validAt: z.string().nullable(),
  /** The target's structured preview (door records, native deployments, evaluations). */
  data: JsonObject,
})
export type AuthoringPreview = z.output<typeof AuthoringPreview>

/** Why a `status` message was posted. */
export const AuthoringPhase = z.enum(['starter', 'draft', 'repair', 'trial_gate', 'refused', 'budget_exhausted', 'model_unavailable', 'blocked', 'timeout', 'needs_agent'])
export type AuthoringPhase = z.output<typeof AuthoringPhase>

/** `status` message body of the loop (role `agent` or `system`). */
export const AuthoringStatusBody = z.object({
  schema: z.literal('authoring-status/1'),
  type: z.literal('authoring_status'),
  /** The user message the run answers. */
  runId: Uuid,
  target: AuthoringTargetId,
  phase: AuthoringPhase,
  /** 0 for statuses posted before the first model call. */
  round: z.number().int().nonnegative(),
  maxRounds: z.number().int().positive(),
  text: z.string(),
  errors: z.array(CompileError),
  /** How to unblock (a refusal), or how to retry. */
  hint: z.string().nullable(),
  mode: z.literal(AUTHORING_MODE),
  /**
   * `needs_agent` (a thread without an agent, e.g. the desk): the compiled program is kept as the preview evidence and
   * nothing is proposed; `createHref` opens the creation flow with the intent prefilled (`/create?intent=…`).
   */
  createHref: z.string().max(4200).optional(),
  /** `needs_agent`: the user's words, to copy into the creation flow. */
  intent: z.string().max(4000).optional(),
})
export type AuthoringStatusBody = z.output<typeof AuthoringStatusBody>

/** `proposal` message body (C1 adds `approvalId`, `approvalKind`); the preview's evidence id is in `evidence_ids`. */
export const AuthoringProposalBody = z.object({
  schema: z.literal('authoring-proposal/1'),
  type: z.literal('authoring_proposal'),
  runId: Uuid,
  target: AuthoringTargetId,
  title: z.string(),
  text: z.string(),
  program: JsonObject,
  /** sha256 of the canonical program. */
  programHash: z.string(),
  preview: AuthoringPreview,
  mode: z.literal(AUTHORING_MODE),
  rounds: z.number().int().nonnegative(),
  starter: z.string().nullable(),
  /** The venue the program trades on (the agent's venue of record); older bodies parse to `robinhood`. */
  venue: Venue.default('robinhood'),
})
export type AuthoringProposalBody = z.output<typeof AuthoringProposalBody>

/**
 * The `authoring` part of an approval payload (`deployment` for strategies, `research` for trend algorithms): what
 * `executeAuthoringApproval` (services/runtime lib/authoring) carries out on approval. Always dry run.
 */
export const AuthoringApproval = z.object({
  runId: Uuid,
  target: AuthoringTargetId,
  programHash: z.string(),
  program: JsonObject,
  mode: z.literal(AUTHORING_MODE),
  /** The venue the program was authored for; execution refuses `VENUE_MISMATCH` when the agent trades elsewhere. */
  venue: Venue.default('robinhood'),
})
export type AuthoringApproval = z.output<typeof AuthoringApproval>

/**
 * A `status` message about something the platform holds for the person: a saved strategy's page
 * (`platform_strategy`: its state, what it reads, its decisions, budget and artifacts, as the App API answers them) or
 * a research program's step (`research_program`: the step, its state and evidence, candidates with their validation).
 * Every line is the platform's answer in words; a part it refused names its blocker, never a zero.
 */
export const PlatformStatusBody = z.object({
  schema: z.literal('platform-status/1'),
  type: z.enum(['platform_strategy', 'research_program']),
  title: z.string().max(200),
  lines: z.array(z.string().max(1000)).max(40),
  facts: z.array(PreviewFact).max(20),
  strategyId: z.string().max(64).nullable(),
  programId: z.string().max(64).nullable(),
  state: z.string().max(64).nullable(),
  /** The platform's references the message cites: artifacts (`art_`), strategies (`stg_`). */
  refs: z.array(z.string().max(64)).max(64),
})
export type PlatformStatusBody = z.output<typeof PlatformStatusBody>
